import { useState, useEffect, useCallback } from 'preact/hooks'
import type { CloudNote } from '../../types'
import { listCloudNotes, upsertCloudNote, deleteCloudNote } from '../../services/api'
import { formatDate, EmptyState, refreshBtnStyle } from './shared'
import { ConfirmDialog, ModalDialog, FormField, CreateButton, InlineInput } from './CrudHelpers'

/** Box note type = 8 */
const BOX_TYPE = 8
const COLUMN_COUNT = 4

/** Flutter post-it palette (lib/screens/box_screen.dart) — kept in lockstep
 *  so values round-trip between the web client and the mobile app. */
const POST_IT_COLORS = [
  '#FFF59D', // Yellow
  '#F48FB1', // Pink
  '#A5D6A7', // Green
  '#90CAF9', // Blue
  '#FFCC80', // Orange
  '#CE93D8', // Purple
]
const DEFAULT_COLOR = POST_IT_COLORS[0]

interface BoxData {
  text: string
  column: number
  orderIndex: number
  color: string // hex like "#FFF59D"
}

/** Flutter stores box notes with `content` as a JSON string:
 *    {"text": "...", "column": 0, "orderIndex": 1, "color": "#FFF59D"}
 *  Parse that, with fallbacks for web-legacy notes where content is plain text. */
function parseBoxContent(content: string | undefined): BoxData {
  if (!content) {
    return { text: '', column: 0, orderIndex: 0, color: DEFAULT_COLOR }
  }
  const trimmed = content.trim()
  if (trimmed.startsWith('{')) {
    try {
      const data = JSON.parse(trimmed)
      if (data && typeof data === 'object') {
        return {
          text: typeof data.text === 'string' ? data.text : '',
          column: typeof data.column === 'number' ? data.column : 0,
          orderIndex: typeof data.orderIndex === 'number' ? data.orderIndex : 0,
          color: typeof data.color === 'string' ? data.color : DEFAULT_COLOR,
        }
      }
    } catch {
      // Fall through to treat as plain text
    }
  }
  return { text: content, column: 0, orderIndex: 0, color: DEFAULT_COLOR }
}

function stringifyBoxContent(data: BoxData): string {
  return JSON.stringify({
    text: data.text,
    column: data.column,
    orderIndex: data.orderIndex,
    color: data.color,
  })
}

/** Mirrors the title-derivation rule in box_screen.dart so the web and
 *  mobile agree on the note.title field. */
function deriveTitle(text: string): string {
  if (!text.trim()) return 'Box note'
  const firstLine = text.split('\n')[0]
  return firstLine.slice(0, Math.min(firstLine.length, 50))
}

export function BoxView({ focusId }: { focusId?: string } = {}) {
  const [notes, setNotes] = useState<CloudNote[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [editColor, setEditColor] = useState(DEFAULT_COLOR)
  const [showCreate, setShowCreate] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setNotes(await listCloudNotes(BOX_TYPE))
    } catch (e) {
      console.error('Failed to load box notes:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Open the focused box note for editing once data is available.
  useEffect(() => {
    if (!focusId || notes.length === 0 || editingId) return
    const match = notes.find((n) => n.id === focusId)
    if (match) {
      const data = parseBoxContent(match.content)
      setEditingId(match.id)
      setEditText(data.text)
      setEditColor(data.color)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, notes])

  const startEdit = (note: CloudNote) => {
    const data = parseBoxContent(note.content)
    setEditingId(note.id)
    setEditText(data.text)
    setEditColor(data.color)
  }

  const handleSave = async (noteId: string) => {
    const note = notes.find((n) => n.id === noteId)
    if (!note) return
    setSaving(true)
    try {
      const existing = parseBoxContent(note.content)
      const nextData: BoxData = {
        text: editText.trim(),
        column: existing.column,
        orderIndex: existing.orderIndex,
        color: editColor,
      }
      const updated: CloudNote = {
        ...note,
        title: deriveTitle(nextData.text),
        content: stringifyBoxContent(nextData),
        last_updated_at: new Date().toISOString(),
      }
      await upsertCloudNote(updated)
      setNotes((prev) => prev.map((n) => n.id === noteId ? updated : n))
      setEditingId(null)
    } catch (e) {
      console.error('Failed to save box note:', e)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (noteId: string) => {
    try {
      await deleteCloudNote(noteId)
      setNotes((prev) => prev.filter((n) => n.id !== noteId))
      setConfirmDelete(null)
      if (editingId === noteId) setEditingId(null)
    } catch (e) {
      console.error('Failed to delete box note:', e)
    }
  }

  /** Organize notes into columns based on the column/orderIndex fields in content JSON. */
  const columns: Array<Array<{ note: CloudNote; data: BoxData }>> = Array.from({ length: COLUMN_COUNT }, () => [])
  for (const note of notes) {
    const data = parseBoxContent(note.content)
    const col = Math.min(Math.max(data.column, 0), COLUMN_COUNT - 1)
    columns[col].push({ note, data })
  }
  for (const col of columns) {
    col.sort((a, b) => a.data.orderIndex - b.data.orderIndex)
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid rgba(250,250,250,0.1)' }}>
        <span style={{ flex: 1, fontSize: 13, color: 'rgba(250,250,250,0.5)' }}>Mind Wall ({notes.length} notes)</span>
        <CreateButton label="New Post-it" onClick={() => setShowCreate(true)} />
        <button onClick={load} style={refreshBtnStyle} disabled={loading}>
          {loading ? '...' : 'Refresh'}
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {loading && <div style={{ padding: 24, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading...</div>}
        {!loading && notes.length === 0 && <EmptyState label="No post-its on the wall" />}
        {!loading && notes.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${COLUMN_COUNT}, 1fr)`, gap: 12, alignItems: 'start' }}>
            {columns.map((col, colIdx) => (
              <div key={colIdx} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {col.map(({ note, data }) => {
                  const bg = data.color || DEFAULT_COLOR
                  const isEditing = editingId === note.id

                  return (
                    <div
                      key={note.id}
                      style={{
                        padding: 12,
                        borderRadius: 8,
                        background: `${bg}20`,
                        border: isEditing ? `1px solid ${bg}` : `1px solid ${bg}40`,
                      }}
                    >
                      {isEditing ? (
                        <>
                          <InlineInput
                            type="textarea"
                            value={editText}
                            onChange={setEditText}
                            placeholder="Content"
                            style={{ minHeight: 60, marginBottom: 8 }}
                          />
                          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                            {POST_IT_COLORS.map((c) => (
                              <div
                                key={c}
                                onClick={() => setEditColor(c)}
                                style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 4,
                                  background: c,
                                  cursor: 'pointer',
                                  border: editColor === c ? '2px solid #fff' : '2px solid transparent',
                                }}
                              />
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              onClick={() => handleSave(note.id)}
                              disabled={saving}
                              style={{ padding: '3px 10px', border: 'none', borderRadius: 4, background: '#2DA5A0', color: '#fff', cursor: 'pointer', fontSize: 12 }}
                            >
                              {saving ? '...' : 'Save'}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              style={{ padding: '3px 10px', border: '1px solid rgba(250,250,250,0.2)', borderRadius: 4, background: 'transparent', color: 'rgba(250,250,250,0.6)', cursor: 'pointer', fontSize: 12 }}
                            >
                              Cancel
                            </button>
                            <span style={{ flex: 1 }} />
                            <button
                              onClick={() => setConfirmDelete(note.id)}
                              style={{ padding: '3px 10px', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 4, background: 'transparent', color: '#f44336', cursor: 'pointer', fontSize: 12 }}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      ) : (
                        <div onClick={() => startEdit(note)} style={{ cursor: 'pointer' }}>
                          <div style={{
                            fontSize: 13,
                            color: 'rgba(250,250,250,0.85)',
                            whiteSpace: 'pre-wrap',
                            overflow: 'hidden',
                            maxHeight: 80,
                            lineHeight: 1.5,
                          }}>
                            {data.text || ''}
                          </div>
                          {data.text.length > 100 && (
                            <div style={{ fontSize: 11, color: bg, marginTop: 4 }}>Click to edit...</div>
                          )}
                          <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.25)', marginTop: 6 }}>
                            {formatDate(note.last_updated_at || note.created_at)}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create post-it modal */}
      {showCreate && (
        <CreateBoxModal
          nextOrderIndex={(col) => {
            const items = columns[Math.min(Math.max(col, 0), COLUMN_COUNT - 1)]
            return items.length > 0 ? Math.max(...items.map((x) => x.data.orderIndex)) + 1 : 0
          }}
          onClose={() => setShowCreate(false)}
          onCreate={async (note) => {
            await upsertCloudNote(note)
            setNotes((prev) => [note, ...prev])
            setShowCreate(false)
          }}
        />
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <ConfirmDialog
          message="Delete this post-it?"
          onConfirm={() => handleDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  )
}

// ── Create Box Note Modal ─────────────────────────────────────────

function CreateBoxModal({ onClose, onCreate, nextOrderIndex }: {
  onClose: () => void
  onCreate: (note: CloudNote) => Promise<void>
  nextOrderIndex: (column: number) => number
}) {
  const [text, setText] = useState('')
  const [color, setColor] = useState(DEFAULT_COLOR)
  const [column, setColumn] = useState('0')
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    if (!text.trim()) return
    setCreating(true)
    try {
      const now = new Date().toISOString()
      const col = Number(column)
      const data: BoxData = {
        text: text.trim(),
        column: col,
        orderIndex: nextOrderIndex(col),
        color,
      }
      const note: CloudNote = {
        id: crypto.randomUUID(),
        title: deriveTitle(data.text),
        content: stringifyBoxContent(data),
        type: BOX_TYPE,
        created_at: now,
        last_updated_at: now,
      }
      await onCreate(note)
    } catch (e) {
      console.error('Failed to create box note:', e)
    } finally {
      setCreating(false)
    }
  }

  return (
    <ModalDialog
      title="New Post-it"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: 'rgba(250,250,250,0.1)', color: 'rgba(250,250,250,0.7)', cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!text.trim() || creating}
            style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: '#2DA5A0', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: !text.trim() || creating ? 0.5 : 1 }}
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </>
      }
    >
      <FormField label="Content" value={text} onChange={setText} type="textarea" placeholder="Post-it content" />
      <FormField label="Column" value={column} onChange={setColumn} type="select" options={[
        { value: '0', label: 'Column 1' },
        { value: '1', label: 'Column 2' },
        { value: '2', label: 'Column 3' },
        { value: '3', label: 'Column 4' },
      ]} />
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginBottom: 4, display: 'block' }}>Color</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {POST_IT_COLORS.map((c) => (
            <div
              key={c}
              onClick={() => setColor(c)}
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: c,
                cursor: 'pointer',
                border: color === c ? '2px solid #fff' : '2px solid transparent',
              }}
            />
          ))}
        </div>
      </div>
    </ModalDialog>
  )
}
